import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';

/**
 * AppMode — the global "what kind of session is this?" switch.
 *
 *   'none' — visitor browsing the site, no wallet, no demo data
 *   'demo' — full UX exploration backed by Mongo/in-game currency
 *   'live' — real Solana devnet wallet connected, real on-chain reads/writes
 *           (admin-gated — only ADMIN_WALLET can enter or stay in live mode)
 *
 * Persisted in localStorage under 'gfg_app_mode'. The legacy sessionStorage
 * 'gfg_demo' flag is kept in sync so older code that hasn't migrated still works.
 */

// Authorized wallets for Live Mode (devnet on-chain access).
// Anyone else who connects gets bounced at the verification modal.
const LIVE_MODE_AUTHORIZED_WALLETS = [
  'C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4', // Admin
  'Dj6c8SG1NwWsD4AxoUf41bXaTg6RsjSBqzrfDz3QFDem', // Authorized devnet collaborator
  'DsN7sYxkspKJu2kbXQv2pVuaq48ybKjZae8iKar7joMe', // Chancellor authority / devnet test wallet
];
const ADMIN_WALLET = LIVE_MODE_AUTHORIZED_WALLETS[0];

const AppModeContext = createContext({
  mode: 'none',
  isDemo: false,
  isLive: false,
  isAdmin: false,
  isLiveAuthorized: false,
  canEnterLive: false,
  connectedWallet: null,
  authorizedWallets: [],
  demoWallet: '',
  enterDemo: () => {},
  enterLive: () => {},
  exit: () => {},
});

export const useAppMode = () => useContext(AppModeContext);
// Back-compat: existing code calls useDemo() — return the same shape with isDemo etc.
export const useDemo = () => {
  const v = useAppMode();
  return {
    isDemo: v.isDemo,
    demoWallet: v.demoWallet,
    enterDemo: v.enterDemo,
    exitDemo: v.exit,
    // Newer hooks surface live-mode state too.
    isLive: v.isLive,
    isAdmin: v.isAdmin,
    canEnterLive: v.canEnterLive,
    mode: v.mode,
    enterLive: v.enterLive,
  };
};

// Persistent base58 demo wallet (Solana-validator-friendly).
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
function generateDemoWallet() {
  const prefix = 'Demo';
  let suffix = '';
  const arr = new Uint8Array(40);
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < 40; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  for (let i = 0; i < 40; i++) suffix += BASE58_ALPHABET[arr[i] % BASE58_ALPHABET.length];
  return prefix + suffix;
}

const VALID = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

export function AppModeProvider({ children }) {
  const { publicKey, connected } = useWallet();
  const connectedWallet = connected && publicKey ? publicKey.toBase58() : null;
  const isAdmin = connectedWallet === ADMIN_WALLET;
  const isLiveAuthorized = !!connectedWallet && LIVE_MODE_AUTHORIZED_WALLETS.includes(connectedWallet);

  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'none';
    // Prefer new key, fall back to legacy demo flag.
    const stored = localStorage.getItem('gfg_app_mode');
    if (stored === 'live' || stored === 'demo' || stored === 'none') return stored;
    if (sessionStorage.getItem('gfg_demo') === 'true') return 'demo';
    return 'none';
  });

  const [demoWallet] = useState(() => {
    if (typeof window === 'undefined') return '';
    let w = localStorage.getItem('gfg_demo_wallet');
    if (!w || !VALID.test(w)) {
      w = generateDemoWallet();
      localStorage.setItem('gfg_demo_wallet', w);
    }
    return w;
  });

  // Keep storage in sync with mode.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('gfg_app_mode', mode);
    if (mode === 'demo') sessionStorage.setItem('gfg_demo', 'true');
    else sessionStorage.removeItem('gfg_demo');
  }, [mode]);

  // ─── Authorization gate for live mode ────────────────────────────────────
  // If a non-authorized wallet is connected and mode is 'live', force back to
  // 'none'. Only wallets in LIVE_MODE_AUTHORIZED_WALLETS may enter or stay
  // in live mode.
  useEffect(() => {
    if (mode !== 'live') return;
    if (!connected) return;
    if (!isLiveAuthorized) {
      setMode('none');
    }
  }, [mode, connected, isLiveAuthorized]);

  const enterDemo = useCallback(() => setMode('demo'), []);
  const enterLive = useCallback(() => {
    // Only authorized wallets can flip to live. Non-authorized → silent no-op.
    // (HomePage's verification modal also enforces this UX-side.)
    if (connected && !isLiveAuthorized) return;
    setMode('live');
  }, [connected, isLiveAuthorized]);
  const exit = useCallback(() => setMode('none'), []);

  const value = {
    mode,
    isDemo: mode === 'demo',
    // Live mode is gated at the flag level — every UI surface that reads
    // `isLive` (DevnetBadge, OnChainChip, live burn banner, on-chain
    // leaderboard fetch, etc.) only "sees" live mode when an authorized
    // wallet is connected. This means non-authorized visitors never see any
    // devnet surfaces even if they hand-edit `gfg_app_mode` in localStorage.
    isLive: mode === 'live' && isLiveAuthorized,
    isAdmin,
    isLiveAuthorized,
    canEnterLive: isLiveAuthorized,
    connectedWallet,
    authorizedWallets: LIVE_MODE_AUTHORIZED_WALLETS,
    demoWallet,
    enterDemo,
    enterLive,
    exit,
  };

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
}

// Back-compat alias so existing imports keep working.
export const DemoProvider = AppModeProvider;
